import { toSafeExternalUrl } from '../../common/safeExternalUrl'
import type { AppItem } from './model'

export interface AppCatalogueScope {
  email: string
  team: string
  company: string
  location: string
}
export type AppCatalogueKind = 'team-apps' | 'services'
export const APP_CATALOGUE_MAX_AGE = 24 * 60 * 60 * 1000
export const APP_CATALOGUE_STALE_TIME = 5 * 60 * 1000
const MAX_ITEMS = 500
const MAX_BYTES = 1_000_000
const optionalFields = ['icon_path', 'visibility', 'company', 'location', 'category_name'] as const

export const appCatalogueScopeKey = (scope: AppCatalogueScope) =>
  JSON.stringify([scope.email.trim().toLowerCase(), scope.team, scope.company, scope.location])
const storageKey = (scope: AppCatalogueScope, kind: AppCatalogueKind) =>
  `home-preview:app-catalogue:v1:${encodeURIComponent(scope.email.trim().toLowerCase())}:${kind}`

// Persist only navigation metadata, never API payload extras, permissions,
// employee details, documents or handbook contents.
function appMetadata(value: unknown): AppItem[] | null {
  if (!Array.isArray(value) || value.length > MAX_ITEMS) return null
  const result: AppItem[] = []
  for (const raw of value) {
    if (!raw || typeof raw !== 'object' || typeof raw.app_name !== 'string' || !raw.app_name.trim() ||
      raw.app_name.length > 300 || typeof raw.app_url !== 'string' || raw.app_url.length > 4096 || !toSafeExternalUrl(raw.app_url)) continue
    const item: AppItem = { app_name: raw.app_name, app_url: raw.app_url }
    for (const key of optionalFields) {
      if (typeof raw[key] === 'string' && raw[key].length <= 4096) item[key] = raw[key]
    }
    result.push(item)
  }
  return result
}

export function readAppCatalogue(scope: AppCatalogueScope, kind: AppCatalogueKind, now = Date.now()): AppItem[] | undefined {
  if (!scope.email.trim()) return undefined
  try {
    const raw = localStorage.getItem(storageKey(scope, kind))
    if (!raw || raw.length > MAX_BYTES) return undefined
    const cache = JSON.parse(raw)
    if (cache?.version !== 1 || cache.scope !== appCatalogueScopeKey(scope) || typeof cache.savedAt !== 'number' ||
      !Number.isFinite(cache.savedAt) || cache.savedAt > now || now - cache.savedAt >= APP_CATALOGUE_MAX_AGE) return undefined
    return appMetadata(cache.items) ?? undefined
  } catch { return undefined }
}

export function writeAppCatalogue(scope: AppCatalogueScope, kind: AppCatalogueKind, value: unknown, now = Date.now()) {
  if (!scope.email.trim()) return
  const metadata = appMetadata(value)
  if (!metadata) return
  const items = metadata.filter(item => item.visibility !== 'inactive' &&
    (item.visibility !== 'admin_only' || scope.team === 'System Development') &&
    (item.company === 'All' || item.company === scope.company && Boolean(scope.company)) &&
    (item.location === 'All' || item.location === scope.location && Boolean(scope.location)))
  try {
    const raw = JSON.stringify({ version: 1, scope: appCatalogueScopeKey(scope), savedAt: now, items })
    if (raw.length <= MAX_BYTES) localStorage.setItem(storageKey(scope, kind), raw)
  } catch { /* Private browsing/storage limits must not prevent the dashboard loading. */ }
}

export function clearAppCatalogue(scope: AppCatalogueScope, kind: AppCatalogueKind) {
  try { localStorage.removeItem(storageKey(scope, kind)) } catch { /* Storage is optional. */ }
}

export function appCatalogueAccessDenied(error: unknown): boolean {
  const status = (error as { status?: number; response?: { status?: number } } | null)?.status ??
    (error as { response?: { status?: number } } | null)?.response?.status
  return status === 401 || status === 403
}
